import React, { FC } from 'react'
import { styled, breakpointDown } from '@moonpig/launchpad-utils'
import { Box, Container, Heading } from '@moonpig/launchpad-components'
import { removeSearchHistoryItem } from '@moonpig/web-core-app-shell-search'
import { system as s } from '@moonpig/launchpad-system'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { useFindLocaleText } from '../../text-localisation'
import { ContactUsCarousel } from '../ContactUsCarousel'
import type { SearchContext } from '../../store/slices/shared'
import { useSearchStore } from '../../store/SearchStore'
import { DepartmentTabs } from '../DepartmentTabs/DepartmentTabs'
import { getSearchTerm } from './getSearchTerm'
import { capitaliseFirstCharacter } from '../../utils'
import { ZeroResultsHeaderVariant } from '../ZeroResultsHeader/ZeroResultsHeader'

const ReservedSpace = styled(Box)`
  height: 24px;
  ${breakpointDown('md')} {
    height: 64px;
  }
`

const StyledContainer = styled(Container)`
  ${s({
    p: 0,
    m: 0,
  })}
`

export type SearchHeaderProps = {
  zeroResults: boolean
  searchContext: SearchContext
  totalCount?: number
  department: DepartmentsEnum
}

export const SearchHeader: FC<SearchHeaderProps> = ({
  zeroResults,
  searchContext,
  department,
}) => {
  const loadingProducts = useSearchStore(store => store.loading)

  const localiseText = useFindLocaleText()

  const {
    searchTerm: originalSearchTerm,
    correctedSearchTerm,
    selectedSuggestion,
  } = searchContext

  const { term, isSearchTermBlank } = getSearchTerm({
    selectedSuggestion,
    searchTerm: originalSearchTerm,
    correctedSearchTerm,
  })

  const showControls = !zeroResults || correctedSearchTerm

  if (zeroResults && department && !loadingProducts) {
    removeSearchHistoryItem({
      searchTerm: term,
      department: department.toLowerCase(),
    })
  }

  const departmentName = `${capitaliseFirstCharacter(
    getDepartmentTitle(department),
  )}`

  return (
    <StyledContainer data-testid="search-header">
      <Box paddingX={0} marginX={{ xs: 6, lg: 10, xxl: 12 }}>
        {loadingProducts ? (
          <ReservedSpace />
        ) : (
          <>
            {(zeroResults || correctedSearchTerm) && (
              <ZeroResultsHeaderVariant
                originalSearchTerm={originalSearchTerm}
                correctedSearchTerm={correctedSearchTerm}
              />
            )}
            {showControls ? (
              <>
                <Heading
                  level="h1"
                  typography={{
                    xs: 'typeMobileDisplay03',
                    md: 'typeMobileDisplay03',
                  }}
                  data-testid="search-term-text"
                >
                  {isSearchTermBlank
                    ? `${localiseText('find.search_header_all')} ${localiseText(
                        'find.department',
                        {
                          departmentName,
                        },
                      )}`
                    : `“${capitaliseFirstCharacter(term)}”`}
                </Heading>
                <ContactUsCarousel searchTerm={term} />
              </>
            ) : (
              <Heading level="h2">
                {localiseText('find.products_you_may_like')}
              </Heading>
            )}
          </>
        )}
      </Box>
      {showControls && (
        <DepartmentTabs
          currentDepartment={department}
          searchContext={searchContext}
        />
      )}
    </StyledContainer>
  )
}
